/**
 * Product master (backend: internal/twin/products.go, migration 0091).
 *
 * A Product is the PART a foundry makes (crankshaft, coupling), distinct from the
 * tooling that makes it. It carries the part's ideal properties (`specs`) and an
 * optional 3D model (`stl_image_id` → the image_blobs row holding the STL). The
 * defect-capture tool loads that model to mark a defect on the 3D surface.
 *
 * Mirrors twin-patterns.ts: thin fetchers + React-Query hooks, live-gated on the
 * build flag + an authenticated, org-resolved session.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { collectAllPages } from "./paging";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

/** One target spec on a chemistry element (OES) — target with an in-spec band. */
export interface ProductCompositionSpec {
  element: string;
  target?: number;
  lsl?: number;
  usl?: number;
  unit?: string;
}

/** One target spec on a mechanical property (tensile / hardness / elongation). */
export interface ProductMechanicalSpec {
  name: string;
  target?: number;
  lsl?: number;
  usl?: number;
  unit?: string;
}

/** The ideal-properties block. Opaque to the backend; shape can grow additively. */
export interface ProductSpecs {
  /**
   * PRIMARY casting-mass anchor: the as-cast (poured) mass of ONE casting, kg,
   * before machining. A pattern cavity's `designed_weight_kg` defaults to this
   * 1:1 — "the product is the anchor". Absent ⇒ read the legacy `mass_kg` as the
   * as-cast mass; never invent one.
   */
  as_cast_mass_kg?: number;
  /**
   * OPTIONAL machined/finished mass of ONE casting, kg. Always ≤ `as_cast_mass_kg`
   * (machining only removes stock). Display + cross-check only — it drives nothing
   * and no allowance is ever derived between it and the as-cast mass.
   */
  finished_mass_kg?: number;
  /**
   * LEGACY pre-anchor mass. Read as the as-cast mass when `as_cast_mass_kg` is
   * absent (ProductSheet migrates it into `as_cast_mass_kg` on next save).
   */
  mass_kg?: number;
  /**
   * What one scrapped casting of this part costs, in the plant's currency: a finite number of 0 or more (the API
   * refuses anything else). Absent: the plant's default cost per rejected casting applies. The brief prices its scrap
   * and the action list its savings with it.
   */
  cost_per_reject?: number;
  /**
   * The cast-iron grade this product is made to (a LABEL — e.g. "FG260",
   * "GJS-450"; no chemistry is implied). Picked from the grade catalog or typed
   * custom. A recipe for this product auto-fills its own grade from this when its
   * grade field is still empty/untouched.
   */
  grade?: string;
  /**
   * The material class this product is cast in (a LABEL — e.g. "grey_iron",
   * "ductile_iron"). A recipe for this product auto-fills its material class from
   * this when that field is still empty/untouched.
   */
  material_class?: string;
  /**
   * OPTIONAL design (methoding) yield %, 0..100 — the fraction of poured metal that
   * becomes casting (the rest is gating + risers). When set, a heat run to a recipe
   * whose PATTERN declares no rigging/yield derives the per-mould rigging from this
   * (rigging = as_cast × (100/yield − 1)) instead of the per-metal template default,
   * so the gating mass is a real product-design number rather than a faked constant.
   * A pattern's own rigging/yield (mig 0112) still OVERRIDES it — tooling methoding
   * wins over the part's nominal. ESTIMATED (a design target), never a measured yield.
   */
  yield_pct?: number;
  composition?: ProductCompositionSpec[];
  mechanical?: ProductMechanicalSpec[];
}

export type ProductStatus = "active" | "retired";

export interface Product {
  id: string;
  org_id: string;
  code: string;
  name: string;
  part_number: string;
  status: ProductStatus;
  specs: ProductSpecs;
  /** image_blobs id of the uploaded STL, when a model exists. */
  stl_image_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProductUpsert {
  id: string;
  code?: string;
  name?: string;
  part_number?: string;
  status?: ProductStatus;
  specs?: ProductSpecs;
  stl_image_id?: string;
}

interface ProductList {
  items: Product[];
  total: number;
  limit: number;
  offset: number;
}

export const twinProducts = {
  list: () => api.get<ProductList>("/v1/twin/products"),
  /** One page (server default 50, max 200). */
  page: (offset: number, limit: number) =>
    api.get<ProductList>(`/v1/twin/products?limit=${limit}&offset=${offset}`),
  /** Every product, walking the pages — a foundry can make hundreds of parts. */
  listAll: async () => {
    const all = await collectAllPages((offset, limit) => twinProducts.page(offset, limit));
    return { items: all.items, total: all.total, truncated: all.truncated };
  },
  get: (id: string) => api.get<Product>(`/v1/twin/products/${id}`),
  upsert: (body: ProductUpsert) => api.post<Product>("/v1/twin/products", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/products/${id}`),
};

const isLiveApi = import.meta.env.VITE_USE_LIVE_API === "true";

function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApi && isAuthenticated && activeOrgId !== null;
}

export const TWIN_PRODUCTS_KEY = ["twin-products"] as const;

/** The active org's products — ALL of them (drives the Products & Tooling screen). */
export function useProductsQuery() {
  return useQuery({
    queryKey: TWIN_PRODUCTS_KEY,
    queryFn: () => twinProducts.listAll(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProductUpsert) => twinProducts.upsert(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_PRODUCTS_KEY });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinProducts.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_PRODUCTS_KEY });
    },
  });
}
